/**
 * Operations Calendar Hooks
 *
 * TanStack Query hooks for calendar events.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCalendarEvents, rescheduleEvent } from '../api';
import type { CalendarEventsParams, RescheduleInput } from '../types';

// =============================================================================
// Query Key Factory
// =============================================================================

export const calendarKeys = {
  all: ['operations-calendar'] as const,
  events: (params: CalendarEventsParams) => [...calendarKeys.all, 'events', params] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch calendar events within a date range
 */
export function useCalendarEvents(params: CalendarEventsParams) {
  return useQuery({
    queryKey: calendarKeys.events(params),
    queryFn: () => fetchCalendarEvents(params),
    staleTime: 30_000, // Consider fresh for 30 seconds
    enabled: !!params.start && !!params.end,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Reschedule a calendar event (project or task)
 */
export function useRescheduleEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RescheduleInput) => rescheduleEvent(input),
    onSuccess: () => {
      // Invalidate all calendar queries
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      // Also invalidate projects and tasks lists since dates changed
      queryClient.invalidateQueries({ queryKey: ['operations-projects'] });
      queryClient.invalidateQueries({ queryKey: ['operations-tasks'] });
    },
  });
}
